import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { Button } from '../ui/button';
import { LogOut, Settings, HelpCircle } from 'lucide-react';
import mainLogo from '../../assets/transparent.main.horizontal.logo.png';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const userMenuRef = useRef(null);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const getUserInitials = (name) => {
    if (!name) return 'U';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const isActiveRoute = (path) => {
    if (path === '/dashboard') {
      return location.pathname === '/dashboard';
    }
    if (path === '/compliance') {
      return location.pathname.startsWith('/violations') || location.pathname.startsWith('/compliance');
    }
    return location.pathname.startsWith(path);
  };

  const getNavLinkClasses = (path) => {
    const baseClasses = "px-3 py-2 text-sm font-medium transition-all duration-200 relative";
    const isActive = isActiveRoute(path);
    
    if (isActive) {
      return `${baseClasses} text-primary after:content-[''] after:absolute after:bottom-0 after:left-0 after:right-0 after:h-[0.125rem] after:bg-primary`;
    }
    
    return `${baseClasses} text-muted-foreground hover:text-foreground hover:after:content-[''] hover:after:absolute hover:after:bottom-0 hover:after:left-0 hover:after:right-0 hover:after:h-[0.125rem] hover:after:bg-muted-foreground/40`;
  };

  const getMobileNavLinkClasses = (path) => {
    const baseClasses = "block px-3 py-2 text-base font-medium transition-all duration-200 relative";
    const isActive = isActiveRoute(path);
    
    if (isActive) {
      return `${baseClasses} text-primary before:content-[''] before:absolute before:left-0 before:top-0 before:bottom-0 before:w-1 before:bg-primary bg-primary/5`;
    }
    
    return `${baseClasses} text-muted-foreground hover:text-foreground hover:before:content-[''] hover:before:absolute hover:before:left-0 hover:before:top-0 hover:before:bottom-0 hover:before:w-1 hover:before:bg-muted-foreground/40 hover:bg-muted/30`;
  };

  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setIsUserMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <header className="bg-background shadow-sm border-b border-border">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center py-1">
          {/* Logo */}
          <div className="flex items-center">
            <Link to={user?.role === 'resident' ? '/announcements' : '/dashboard'} className="flex items-center">
              <img 
                src={mainLogo} 
                alt="HOABuddy Logo" 
                className="w-[160px] min-w-[160px] h-auto rounded-lg"
              />
            </Link>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex flex-1 items-center ml-8">
            <div className="flex space-x-8">
              {(user?.role === 'board' || user?.role === 'admin') && (
                <Link to="/dashboard" className={getNavLinkClasses('/dashboard')}>
                  Dashboard
                </Link>
              )}
              <Link to="/announcements" className={getNavLinkClasses('/announcements')}>
                Announcements
              </Link>
              <Link to="/requests" className={getNavLinkClasses('/requests')}>
                Requests
              </Link>
              <Link to="/documents" className={getNavLinkClasses('/documents')}>
                Documents
              </Link>
              <Link to="/compliance" className={getNavLinkClasses('/compliance')}>
                Compliance
              </Link>
              <Link to="/dues" className={getNavLinkClasses('/dues')}>
                Dues
              </Link>
              {(user?.role === 'board' || user?.role === 'admin') && (
                <>
                  <Link to="/users" className={getNavLinkClasses('/users')}>
                    Users
                  </Link>
                  <Link to="/addresses" className={getNavLinkClasses('/addresses')}>
                    Addresses
                  </Link>
                </>
              )}
            </div>
          </nav>

          {/* User Menu */}
          <div className="flex items-center space-x-4">
            {/* User Avatar Dropdown */}
            <div className="relative" ref={userMenuRef}>
              <Button
                onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                variant="ghost"
                size="sm"
                className="flex items-center space-x-2 p-2"
              >
                <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center">
                  <span className="text-primary-foreground font-medium text-sm">
                    {getUserInitials(user?.name)}
                  </span>
                </div>
              </Button>

              {/* Dropdown Menu */}
              {isUserMenuOpen && (
                <div className="absolute right-0 mt-2 w-64 bg-background border border-border rounded-md shadow-lg z-50">
                  <div className="py-1">
                    {/* User Info */}
                    <div className="px-4 py-2 border-b border-border">
                      <p className="text-sm font-medium text-foreground break-words">{user?.name}</p>
                      <p className="text-xs text-muted-foreground break-all">{user?.email}</p>
                    </div>
                    
                    {/* Profile Link */}
                    <Link
                      to="/profile"
                      className="w-full text-left px-4 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-muted flex items-center space-x-2"
                      onClick={() => setIsUserMenuOpen(false)}
                    >
                      <Settings className="h-4 w-4" />
                      <span>Profile</span>
                    </Link>

                    {/* Support Link */}
                    <Link
                      to="/support"
                      className="w-full text-left px-4 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-muted flex items-center space-x-2"
                      onClick={() => setIsUserMenuOpen(false)}
                    >
                      <HelpCircle className="h-4 w-4" />
                      <span>Support</span>
                    </Link>
                    
                    {/* Logout Button */}
                    <button
                      onClick={() => {
                        setIsUserMenuOpen(false);
                        handleLogout();
                      }}
                      className="w-full text-left px-4 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-muted flex items-center space-x-2"
                    >
                      <LogOut className="h-4 w-4" />
                      <span>Logout</span>
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Mobile menu button */}
            <Button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              variant="ghost"
              size="icon"
              className="md:hidden"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {isMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </Button>
          </div>
        </div>

                  {/* Mobile Navigation */}
          {isMenuOpen && (
            <nav className="md:hidden py-4 border-t border-border">
              <div className="space-y-2">
                {(user?.role === 'board' || user?.role === 'admin') && (
                  <Link to="/dashboard" className={getMobileNavLinkClasses('/dashboard')} onClick={() => setIsMenuOpen(false)}>
                    Dashboard
                  </Link>
                )}
                <Link to="/announcements" className={getMobileNavLinkClasses('/announcements')} onClick={() => setIsMenuOpen(false)}>
                  Announcements
                </Link>
                <Link to="/requests" className={getMobileNavLinkClasses('/requests')} onClick={() => setIsMenuOpen(false)}>
                  Requests
                </Link>
                <Link to="/documents" className={getMobileNavLinkClasses('/documents')} onClick={() => setIsMenuOpen(false)}>
                  Documents
                </Link>
                <Link to="/compliance" className={getMobileNavLinkClasses('/compliance')} onClick={() => setIsMenuOpen(false)}>
                  Compliance
                </Link>
                <Link to="/dues" className={getMobileNavLinkClasses('/dues')} onClick={() => setIsMenuOpen(false)}>
                  Dues
                </Link>
                {(user?.role === 'board' || user?.role === 'admin') && (
                  <>
                    <Link to="/users" className={getMobileNavLinkClasses('/users')} onClick={() => setIsMenuOpen(false)}>
                      Users
                    </Link>
                    <Link to="/addresses" className={getMobileNavLinkClasses('/addresses')} onClick={() => setIsMenuOpen(false)}>
                      Addresses
                    </Link>
                  </>
                )}
              
              {/* Mobile User Menu */}
              <div className="border-t border-border pt-2 mt-2">
                <div className="px-3 py-2">
                  <p className="text-sm font-medium text-foreground break-words">{user?.name}</p>
                  <p className="text-xs text-muted-foreground break-all">{user?.email}</p>
                </div>
                <Link
                  to="/profile"
                  className="w-full text-left px-3 py-2 text-base font-medium text-muted-foreground hover:text-foreground flex items-center space-x-2"
                  onClick={() => setIsMenuOpen(false)}
                >
                  <Settings className="h-4 w-4" />
                  <span>Profile</span>
                </Link>
                <Link
                  to="/support"
                  className="w-full text-left px-3 py-2 text-base font-medium text-muted-foreground hover:text-foreground flex items-center space-x-2"
                  onClick={() => setIsMenuOpen(false)}
                >
                  <HelpCircle className="h-4 w-4" />
                  <span>Support</span>
                </Link>
                <button
                  onClick={() => {
                    setIsMenuOpen(false);
                    handleLogout();
                  }}
                  className="w-full text-left px-3 py-2 text-base font-medium text-muted-foreground hover:text-foreground flex items-center space-x-2"
                >
                  <LogOut className="h-4 w-4" />
                  <span>Logout</span>
                </button>
              </div>
            </div>
          </nav>
        )}
      </div>
    </header>
  );
};

export default Header; 